import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ImageIcon,
  X,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  Upload
} from "lucide-react";

const SubmitGallery = () => {
  const [altText, setAltText] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [author, setAuthor] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [requests, setRequests] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // Animation states
  const [animatePage, setAnimatePage] = useState(false);

  useEffect(() => {
    setAnimatePage(true);
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('currentUser'));
      if (user && user.email) {
        const res = await axios.get(
          `https://aaapd.org.et/gallery_backend/api/getMyRequests.php?email=${encodeURIComponent(user.email)}`
        );
        setRequests(Array.isArray(res.data) ? res.data : []);
      } else {
        setRequests([]);
        showMessage("User not authenticated. Please log in again.", "error");
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      setRequests([]);
      showMessage("Failed to fetch your gallery requests.", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(""), 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!imageFile || !author) {
      showMessage("Please upload an image and provide your name.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("image", imageFile);
    formData.append("alt_text", altText);
    formData.append("description", description);
    formData.append("author", author);

    // Add OPD ID for filtering - get from backend using email
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user && user.email) {
      formData.append("email", user.email);
    }

    setFormLoading(true);
    setMessage("");

    try {
      const res = await axios.post(
        "https://aaapd.org.et/gallery_backend/api/addImageOPD.php",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.status === "success") {
        showMessage("Image submitted successfully and pending admin approval.");
        setAltText("");
        setDescription("");
        setImageFile(null);
        setAuthor("");
        fetchRequests();
        setShowForm(false);
      } else {
        showMessage(res.data.message || "Error submitting image.", "error");
      }
    } catch (error) {
      console.error(error);
      showMessage("An error occurred while submitting.", "error");
    }

    setFormLoading(false);
  };

  return (
    <div
      className={`bg-gray-50 min-h-screen p-6 transition-opacity duration-1000 ease-in-out ${
        animatePage ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">My Gallery Requests</h1>
            <p className="text-gray-600 mt-1">View your submitted images and submit new requests</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 shadow-md hover:shadow-lg mt-6 sm:mt-8"
          >
            <PlusCircle className="w-5 h-5" />
            Submit New Request
          </button>
        </header>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              messageType === "error"
                ? "bg-red-100 border-l-4 border-red-500 text-red-700"
                : "bg-green-100 border-l-4 border-green-500 text-green-700"
            }`}
          >
            <div className="flex items-center gap-2">
              {messageType === "error" ? (
                <AlertCircle className="w-5 h-5" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              {message}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="flex items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              <p className="text-gray-600">Loading your gallery requests...</p>
            </div>
          </div>
        ) : (
          <>
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions found</h3>
                <p className="mt-1 text-sm text-gray-500">You haven't submitted any gallery images yet.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 mx-auto"
                  >
                    <PlusCircle className="w-5 h-5" />
                    Submit Your First Request
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white shadow overflow-hidden rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Your Submissions</h3>
                  <p className="text-sm text-gray-500">Track the status of your gallery image requests</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Preview
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Alt Text
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {requests.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex-shrink-0 h-16 w-16">
                              <img
                                className="h-16 w-16 rounded-md object-cover border border-gray-200"
                                src={`https://aaapd.org.et/gallery_backend/images_opd/${req.file_name}`}
                                alt={req.alt_text || 'Gallery image'}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                              {req.alt_text || <span className="text-gray-400">No alt text</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-500 max-w-md truncate">
                              {req.description || <span className="text-gray-400">No description</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              req.status === "approved"
                                ? "bg-green-100 text-green-800"
                                : req.status === "rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}>
                              {req.status}
                            </span>
                          </td>
                          
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Enhanced Modal Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg relative transform transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Upload className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-800">Submit Gallery Image</h2>
                  </div>
                  <button
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowForm(false)}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {message && (
                  <div
                    className={`p-4 rounded-lg ${
                      messageType === "error"
                        ? "bg-red-100 border-l-4 border-red-500 text-red-700"
                        : "bg-green-100 border-l-4 border-green-500 text-green-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {messageType === "error" ? (
                        <AlertCircle className="w-5 h-5" />
                      ) : (
                        <CheckCircle className="w-5 h-5" />
                      )}
                      {message}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name / OPD Name *
                    </label>
                    <input
                      type="text"
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter your name or OPD name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alt Text
                    </label>
                    <input
                      type="text"
                      value={altText}
                      onChange={(e) => setAltText(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Brief description for accessibility"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                    placeholder="Provide a detailed description of your image"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image File *
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading || !imageFile || !author}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                  >
                    {formLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Submit for Approval
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitGallery;
